import { Inject, Injectable, OnDestroy } from '@angular/core';

import { PartConfig, TemplateConfig
       } from 'app/collage-designer/collage-designer-config';
import { GalleryService } from 'app/gallery/gallery.service';
import { DesignStepService
       } from 'collage-designer/design-step/design-step.service';
import { DesignState } from 'collage-designer/stage/stage-config';
import { ConfigService } from 'core/config/config.service';
import { ToolboxConstants } from 'core/constants';
import { Subscription } from 'rxjs';

type DesignedPartUrl = string;

interface TemplatedPart {
  templateLocation: string;
  placeholders: Array<RenderRequestPlaceholder>;
}

interface RenderRequestPlaceholder {
  location: string;
}

// NOTE: needed for FILL_WITH_GALLERY_IMAGES only.
//       not used right now. When (and if) we want to show custom preview
//       images for motive templates, this location should be provided
//       somehow or read from the stage.
const PLACEHOLDER_IMAGE_URL = "https://resi.puzzleyou.net/images/placeholder";

export class MissingStructureError extends Error {}

@Injectable()
export class PrnglService
{
  private prnglUrl?: string;

  constructor(
      @Inject(DesignStepService) private designStepService: DesignStepService,
      @Inject(ConfigService) private configService: ConfigService,
      @Inject(GalleryService) private galleryService: GalleryService,
  )
  {
    this.prnglUrl = this.configService
        .getProductDesignerComponentConfig().prnglURL;
  }

  public makeRenderRequest(
    productId: string, previewType: string): RenderRequest
  {
    return new RenderRequest(
      this,
      this.designStepService,
      this.galleryService,
      productId,
      previewType
    );
  }

  public get isAvailable(): boolean
  {
    return !!this.prnglUrl;
  }

  public makeRenderRequestUrl(request: RenderRequest): string | undefined
  {
    if (! this.prnglUrl) {
      return;
    }

    const ppr = encodeURIComponent(
      JSON.stringify(request.productPreviewRequest));
    const viewport = encodeURIComponent(
      JSON.stringify(request.viewport));

    return (
      `${this.prnglUrl}/products/${request.productId}/${request.previewType}` +
      `?product_preview_request=${ppr}` +
      `&viewport=${viewport}` +
      `&persist=${request.persist}`
    );
  }

  public snapToViewportStep(size: number): number
  {
    const SIZES = [128, 256, 512, 1024, 2048];
    return SIZES.find((s) => s >= size) || Math.max( ...SIZES );
  }
}

enum RenderRequestPartKind {
  DESIGNED_URL,
  TEMPLATED,
}

class RenderRequestPart {
  private constructor(
    private kind: RenderRequestPartKind,
    private url?: string,
    private templated?: TemplatedPart,
  )
  { }

  public static fromDesignedPartUrl(url: string): RenderRequestPart
  {
    return new RenderRequestPart(
      RenderRequestPartKind.DESIGNED_URL,
      url,
      undefined
    );
  }

  public static fromTemplatedPart(part: TemplatedPart): RenderRequestPart
  {
    return new RenderRequestPart(
      RenderRequestPartKind.TEMPLATED,
      undefined,
      part
    );
  }

  public get productPreviewRequestPart(): Object
  {
    switch (this.kind) {
      case RenderRequestPartKind.DESIGNED_URL:
        return this.url;
      case RenderRequestPartKind.TEMPLATED:
        return {
          'template_location': this.templated.templateLocation,
          'placeholders': this.templated.placeholders,
        };
      default:
        return undefined; // unreachable but tslint complains
    }
  }
}

export class RenderRequest {
  public persist: boolean;
  public viewportLongSide: number;
  // TODO quality (prngl: supersampling, different task)
  private parts: {[key: string]: RenderRequestPart};

  constructor(
    private prnglService: PrnglService,
    private designStepService: DesignStepService,
    private galleryService: GalleryService,
    public productId: string,
    public previewType: string,
  )
  {
    this.persist = false;
    this.viewportLongSide = 512;
    this.parts = {};
  }

  public fillDesignedParts()
  {
    for (const state of this
         .designStepService
         .getDesignStates()
         .filter((it) => !!it.uploadedStructureUrl))
     {
       const part = this.designStepService.getDesignablePart(state);

       if (!!part) {
         this.addDesignedPart(part.id, state.uploadedStructureUrl);
       }
     }
  }

  public addDesignedPart(id: string, partUrl: string)
  {
    this.parts[id] = RenderRequestPart.fromDesignedPartUrl(partUrl);
  }

  public addGalleryImagesPart(id: string, template: TemplateConfig)
  {
    const galleryImages = this.galleryService.getImages();
    const numImagesNeeded =
        template.imagePlaceholders.landscape
        + template.imagePlaceholders.portrait;
    const pprPlaceholders = galleryImages
        .slice(0, numImagesNeeded)
        .map(i => ({'location': i.url}));

    const imagesMissing = numImagesNeeded - pprPlaceholders.length;

    if (imagesMissing > 0) {
      const placeholders: Array<RenderRequestPlaceholder> =
        new Array(imagesMissing);
      placeholders.fill({"location": PLACEHOLDER_IMAGE_URL});
      pprPlaceholders.push(...placeholders);
    }

    this.parts[id] = RenderRequestPart.fromTemplatedPart({
      templateLocation: RenderRequest.templateLocation(template),
      placeholders: pprPlaceholders,
    });
  }

  /**
   * @throws MissingStructureError
   */
  public addMotiveStructurePart(id: string, template: TemplateConfig): void
  {
    const motiveStep = this.designStepService
      .getDesignStates()
      .filter((it) => !! it.uploadedStructureUrl)
      .find((it) => {
        const part = this.designStepService.getDesignablePart(it);
        return !!part && part.id === 'motive';
      });

    if (! motiveStep) {
      throw new MissingStructureError(
          'No uploaded structure for part "motive" available.');
    }

    const pprPlaceholders = [
      { 'location': motiveStep.uploadedStructureUrl }
    ];

    this.parts[id] = RenderRequestPart.fromTemplatedPart({
      templateLocation: RenderRequest.templateLocation(template),
      placeholders: pprPlaceholders,
    });
  }

  private static templateLocation(template: TemplateConfig): string
  {
    return template.stageConfig?.initialSvgStructureURL
        || template.stageConfig?.svgStructureURL;
  }

  public get url(): string | undefined
  {
    return this.prnglService.makeRenderRequestUrl(this);
  }

  public get productPreviewRequest(): Object
  {
    let pprParts = {};

    for (const partId of Object.keys(this.parts)) {
      pprParts[partId] = this.parts[partId].productPreviewRequestPart;
    }

    return { parts: pprParts };
  }

  public get viewport(): Object
  {
    return {
      "long_side": this.viewportLongSide,
    };
  }
}
